import React, { Fragment, useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Box,
  Dialog,
  DialogContent,
  Typography,
  Grid,
  Button,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { ButtonContainer, SubheaderSection, TextField } from "../../components";
import { Formik } from "formik";
import * as Yup from "yup";

import { useNavigate } from "react-router-dom";
import pages from "../../constants/pages";
import { useAxios } from "../../hooks";
import { setUserName } from "../../redux/slices/memberSlice";
import { alert } from "../../redux/slices/alertSlice";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "5.5px",
    background: "rgb(255, 255, 255)",
    [theme.breakpoints.up("sm")]: {
      width: "85%",
    },
    [theme.breakpoints.up("md")]: {
      width: "50%",
    },
    [theme.breakpoints.up("lg")]: {
      width: "35%",
    },
  },
}));

const ContentBox = styled(Box)({
  padding: "20px",
  textAlign: "center",
  alignContent: "center",
  alignItems: "center",
});

const NewUserDetails = ({}) => {
  const axios = useAxios();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { visitorData, authToken } = useSelector(
    (state) => state.memberDetails
  );

  // Extract URL parameters
  const queryParams = new URLSearchParams(window.location.search);
  const bookingID = queryParams.get("booking_id");
  const entityID = queryParams.get("hotel_id");

  const [popup, setPopup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    visitors: [
      {
        name: "",
        email_id: "",
        phone_number: "",
        guestType: 0,
      },
    ],
  });

  // Show email field if both bookingID and entityID are present
  const showEmailField = bookingID && entityID;

  // Dynamic validation schema based on whether email is required
  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    email: showEmailField
      ? Yup.string()
          .email("Invalid email address")
          .required("Email is required")
      : Yup.string().email("Invalid email address"),
  });

  useEffect(() => {
    setPopup(
      !visitorData.phoneNumber ? false : !!visitorData.firstName ? false : true
    );
  }, [visitorData]);

  return (
    <StyledDialog open={popup} fullWidth maxWidth="md">
      <DialogContent style={{ padding: "0px" }}>
        <ContentBox>
          <SubheaderSection
            textAlign="start"
            variant="h5"
            title={`Enter your name as on your government ID`}
            titleColor="#2b2b2b"
            titleProps={{ fontWeight: 700 }}
          />
          <Grid container>
            <Formik
              initialValues={{ name: "", email: "" }}
              validationSchema={validationSchema}
              onSubmit={(formValues, { setSubmitting }) => {
                setLoading(true);
                const name = formValues.name.split(" ");
                axios({
                  url:
                    "/admin/profile/edit-profile?visitor_id=" + visitorData.ID,
                  method: "POST",
                  data: {
                    first_name: name[0] || "",
                    last_name: name.slice(1).join(" ") || "",
                  },
                  customAuthToken: sessionStorage.getItem("authToken"),
                })
                  .then((response) => {
                    if (response.status) {
                      dispatch(setUserName(formValues.name));

                      if (entityID) {
                        const name = formValues.name.split(" ");
                        // After profile update, call hotel guest add API
                        const guestList = [
                          {
                            name: formValues.name,
                            first_name: name[0] || "",
                            last_name: name.slice(1).join(" ") || "",
                            phone_number:
                              sessionStorage.getItem("mobileNumber") || "",
                            email_id: formValues.email || "",
                            is_dependent: false,
                          },
                        ];

                        axios({
                          url: "hotel/booking/guest/add",
                          method: "POST",
                          data: {
                            booking_ref_id: bookingID,
                            hotel_id: entityID,
                            guests: guestList,
                          },
                          disableError: true,
                          disableRedirect: true,
                          customAuthToken: sessionStorage.getItem("authToken"),
                        })
                          .then((guestResponse) => {
                            setLoading(false);
                            if (guestResponse.status) {
                              setPopup(false);

                              navigate(
                                `${pages.PhotoVerification?.route}?visitor_id=${visitorData.ID}&doc_face=true&booking_id=${bookingID}&entity_id=${entityID}&user_type_id=5`
                              );
                            }
                          })
                          .catch((error) => {
                            setLoading(false);
                            dispatch(
                              alert({
                                message: "Failed to add guest",
                                type: "error",
                              })
                            );
                          });
                      } else {
                        setLoading(false);
                        navigate(
                          sessionStorage.getItem("previousPage") ||
                            pages.home.route
                        );
                        setPopup(false);
                      }
                    } else {
                      setLoading(false);
                    }
                  })
                  .catch((error) => {
                    setLoading(false);
                    dispatch(
                      alert({
                        message: "Failed to update profile",
                        type: "error",
                      })
                    );
                  });
              }}
            >
              {(formikProps) => {
                return (
                  <Fragment>
                    <Grid item container>
                      <TextField
                        name="name"
                        size="small"
                        placeholder="Enter Your Name"
                        formikProps={formikProps}
                      ></TextField>
                    </Grid>

                    {showEmailField && (
                      <Grid item container mt={2}>
                        <TextField
                          name="email"
                          size="small"
                          placeholder="Enter Your Email"
                          formikProps={formikProps}
                        ></TextField>
                      </Grid>
                    )}

                    <Grid item container justifyContent={"end"} mt={2}>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={
                          !formikProps.dirty || !formikProps.isValid || loading
                        }
                        onClick={formikProps.handleSubmit}
                      >
                        {loading ? "Submitting..." : "Submit"}
                      </Button>
                    </Grid>
                  </Fragment>
                );
              }}
            </Formik>
            <Grid></Grid>
          </Grid>
        </ContentBox>
      </DialogContent>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 99999 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </StyledDialog>
  );
};

export default NewUserDetails;
